import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useMarkNotificationsRead, useNotifications } from '@/hooks/notificationQueries';
import { IconBell, IconX } from '@/components/Icons';
import type { AppNotification } from '@/lib/types';
import './notifications.css';

interface NotificationBellProps {
  /** Onde o sino mora: no topo (celular) ou no rodapé do menu (computador). */
  placement: 'topbar' | 'sidebar';
  /** Menu recolhido: no rodapé, só o ícone. */
  collapsed?: boolean;
}

/** "agora", "há 5 min", "há 2 h", ou o dia e a hora. */
function when(iso: string, now = Date.now()): string {
  const minutes = Math.round((now - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'agora';
  if (minutes < 60) return `há ${minutes} min`;
  if (minutes < 24 * 60) return `há ${Math.round(minutes / 60)} h`;
  return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/** Aviso do sistema (fora da aba), se a pessoa permitiu. Nunca quebra a página. */
function systemNotify(items: readonly AppNotification[]) {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    for (const item of items.slice(0, 3)) new Notification(item.title, { body: item.body, tag: item.id });
  } catch {
    // Navegador sem suporte (alguns celulares): fica só o sino.
  }
}

/**
 * O sino: para o administrador, o sinal disparado e o fim de cada operação; para o
 * membro, a venda reprovada na conferência.
 * Abrir marca tudo como lido; os que eram novos continuam destacados até fechar.
 */
export function NotificationBell({ placement, collapsed = false }: NotificationBellProps) {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { data } = useNotifications();
  const markRead = useMarkNotificationsRead();
  const [open, setOpen] = useState(false);
  const [unreadIds, setUnreadIds] = useState<Set<string>>(new Set());
  const [permission, setPermission] = useState(() => (typeof Notification === 'undefined' ? 'unsupported' : Notification.permission));
  const seen = useRef<Set<string> | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const items = data?.items ?? [];
  const unread = data?.unread ?? 0;

  // Avisos que chegaram desde a última consulta viram aviso do sistema. Só o
  // sino do menu dispara (ele está sempre montado), para não sair repetido.
  useEffect(() => {
    if (!data || placement !== 'sidebar') return;
    const ids = new Set(data.items.map((item) => item.id));
    if (seen.current) systemNotify(data.items.filter((item) => !item.read && !seen.current!.has(item.id)));
    seen.current = ids;
  }, [data, placement]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    const onClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!panelRef.current?.contains(target) && !buttonRef.current?.contains(target)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    panelRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  function toggle() {
    if (open) return setOpen(false);
    setUnreadIds(new Set(items.filter((item) => !item.read).map((item) => item.id)));
    setOpen(true);
    if (unread > 0) markRead.mutate();
  }

  function go(item: AppNotification) {
    setOpen(false);
    if (item.link) navigate(item.link);
  }

  async function allowSystem() {
    try {
      setPermission(await Notification.requestPermission());
    } catch {
      setPermission('denied');
    }
  }

  const label = unread > 0 ? `Avisos: ${unread} ${unread === 1 ? 'novo' : 'novos'}` : 'Avisos';

  return (
    <div className="notif" data-placement={placement}>
      <button
        ref={buttonRef}
        type="button"
        className={placement === 'topbar' ? 'icon-btn notif-trigger' : 'navbtn notif-trigger'}
        onClick={toggle}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={collapsed || placement === 'topbar' ? label : undefined}
      >
        <span className="notif-icon">
          <IconBell size={placement === 'topbar' ? 20 : undefined} />
          {unread > 0 ? <span className="notif-badge num" aria-hidden>{unread > 9 ? '9+' : unread}</span> : null}
        </span>
        {placement === 'sidebar' ? <span className="navbtn-label">Avisos</span> : null}
      </button>

      {open ? (
        <div ref={panelRef} className="notif-panel" role="dialog" aria-label="Avisos" tabIndex={-1}>
          <div className="notif-head">
            <strong>Avisos</strong>
            <button type="button" className="icon-btn" onClick={() => setOpen(false)} aria-label="Fechar os avisos">
              <IconX size={16} />
            </button>
          </div>
          {items.length === 0 ? (
            <p className="notif-empty muted">
              {isAdmin
                ? 'Nada por aqui. O sinal disparado e o fim de cada operação de alavancagem aparecem aqui.'
                : 'Nada por aqui. Se uma venda sua for reprovada na conferência, o aviso aparece aqui.'}
            </p>
          ) : (
            <ul className="notif-list">
              {items.map((item) => (
                <li key={item.id}>
                  <button type="button" className="notif-item" data-new={unreadIds.has(item.id) ? 'true' : undefined} data-kind={item.kind} onClick={() => go(item)}>
                    <span className="notif-title">{item.title}</span>
                    <span className="notif-body">{item.body}</span>
                    <span className="notif-when muted">{when(item.createdAt)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {permission === 'default' ? (
            <div className="notif-foot">
              <button type="button" className="btn btn-sm" onClick={() => void allowSystem()}>Avisar também fora da aba</button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
